// LOGOMAN PRO — WCAG contrast utility.
// Used in the renderer so previews stay readable regardless of color source.

/** WCAG relative luminance of an sRGB hex color (0..1). */
export function relativeLuminance(hex: string): number {
  const m = /^#?([\da-f]{6})$/i.exec(hex);
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two hex colors (1..21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

function blendHex(hex: string, target: "black" | "white", amount: number): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  const t = target === "black" ? 0 : 255;
  const a = Math.max(0, Math.min(1, amount));
  const blend = (c: number) => Math.round(c * (1 - a) + t * a);
  const nr = blend(r), ng = blend(g), nb = blend(b);
  return `#${((nr << 16) | (ng << 8) | nb).toString(16).padStart(6, "0")}`;
}

/**
 * Return a foreground color that meets `minRatio` contrast against `bg`.
 * If the original already passes, returns it unchanged.
 * Otherwise blends toward black (on light bg) or white (on dark bg) in steps,
 * falling back to pure black/white if needed.
 */
export function ensureContrast(fg: string, bg: string, minRatio = 4.5): string {
  if (contrastRatio(fg, bg) >= minRatio) return fg;
  const bgIsLight = relativeLuminance(bg) > 0.5;
  const target: "black" | "white" = bgIsLight ? "black" : "white";
  for (const step of [0.15, 0.3, 0.45, 0.6, 0.75, 0.9]) {
    const candidate = blendHex(fg, target, step);
    if (contrastRatio(candidate, bg) >= minRatio) return candidate;
  }
  return target === "black" ? "#000000" : "#ffffff";
}
